// The bout player, used for a live bout and for a replay alike. Rules:
//   1. A turn is shown the moment it arrives. The typewriter is cosmetic and never
//      makes the viewer late: turns queue and each streams out in full — a backlog
//      streams brisker (TYPE_CAP_BACKLOG) but nothing ever snaps or jumps in whole.
//   2. Between turns a quiet "…" sits where the next message will land.
//   3. At the end the bout is sealed (seal.js) and the stage plays the fall; the channel
//      changes once the stage is clear and the next bout is on (see tv.js).
// Live feeds the player from the bout's event stream; a replay (replay.js) feeds it the
// turns of an old bout, paced by hand, so both look the same. The walk-on and the salt
// are the stage's own (static/js/clouds.js), whenever a new pair comes on.
import { $, msgHtml, setClock, reduced, parseEchoAttr } from "./dom.ts";
import { typeInto, focusMsg, unfocusAll } from "./typewriter.ts";
import { keepInView, followWhile } from "./follow.ts";
import { ring, isTv } from "./tv.ts";
import { sealBout } from "./seal.ts";
import { music as band } from "./sound/music.ts";
import { T } from "./timing.ts";
import { tap } from "./sound.ts";
import { limits, other, parseDone, parseKotodama, parseTurn } from "./wire.ts";
import { rain } from "./kotodama.ts";
import { wantStage } from "./show.ts";

/** Where the player is. idle nothing yet (a page with turns already on it starts "thinking") typing a turn is typing thinking between turns: the dots are up finishing the last turn has typed; a beat to read it before the seal ended sealed */
type PlayerState = "idle" | "typing" | "thinking" | "finishing" | "ended";

/** The timers a player may have running, each cleared in one place. */
class Timers {
  readBeat: ReturnType<typeof setTimeout> | null;
  turnGap: ReturnType<typeof setTimeout> | null;
  hajime: ReturnType<typeof setTimeout> | null;
  constructor() {
    this.readBeat = null;
    this.turnGap = null;
    this.hajime = null;
  }
  clearAll() {
    for (const k of ["readBeat", "turnGap", "hajime"] as const) {
      const t = this[k];
      if (t) clearTimeout(t);
      this[k] = null;
    }
  }
}

/**
 * How long a turn may type: the decisive turn (the last one, bout over) always types
 * properly, it is the one worth reading; a backlog means we are late: one waiting, type
 * quick; several waiting, just show it.
 */
export const typeCapFor = (queued: number, lastOfBout: boolean, full: number) =>
  lastOfBout ? full : queued ? T.TYPE_CAP_BACKLOG : full;

type PlayerOpts = {
  /** the longest a turn types */
  cap?: number;
  /** the queue ran dry after a turn (the thinking dots are up) */
  onIdle?: () => void;
  /** leave the state label alone at the end (a replay stays "replay") */
  keepState?: boolean;
  /** hold the words until the referee's wave (`basho:hajime` from the stage), or T.HAJIME_FALLBACK: the opening ceremony plays first */
  hold?: boolean;
};

/**
 * Take back any turns the caller wants to replay before making the player.
 */
export function player(bout: HTMLElement, tv: TvHooks, opts: PlayerOpts = {}) {
  const list = $("[data-transcript]", bout) as HTMLElement;
  const clock = $("[data-turn]", bout);
  const stateEl = $(".state", bout);
  const { maxTokens } = limits(bout);
  // the broadcast's score: the home page only (a transcript page is read, not watched); replays too
  const music = isTv() ? band : null;
  const slugOf = (side: Side) =>
    ($(`.tachiai .side.${side} a.name`, bout) as HTMLAnchorElement | null)?.pathname.split("/").pop() || side;
  const queue: TurnEvent[] = [];
  let typing: ReturnType<typeof typeInto> | null = null;
  let done: DoneEvent | null = null;
  let thinking: HTMLElement | null = null;
  const existing = list.querySelector("li.msg:last-child") as HTMLElement | null;
  let lastSide: Side | null = existing ? (existing.dataset.side as Side) : null;
  let state: PlayerState = "idle";
  const timers = new Timers();
  // the opening: the turns wait in the queue until the referee's wave (the stage's basho:hajime), so no word is typed under the ceremony
  let held = !!opts.hold;
  const release = () => {
    if (!held) return;
    held = false;
    if (timers.hajime) {
      clearTimeout(timers.hajime);
      timers.hajime = null;
    }
    bout.removeEventListener("basho:hajime", release);
    if (!typing) pump();
  };
  if (held) {
    bout.addEventListener("basho:hajime", release);
    timers.hajime = setTimeout(release, T.HAJIME_FALLBACK);
  }
  // the secret word (#543): its rain waits until its turn is on the screen (typed, or passed by a later one)
  const words = new Map<number, KotodamaEvent>();
  let shownUpTo = existing ? +(existing.dataset.turn || 0) : 0;
  const rained = new Set<number>(); // a resumed stream replays the event the broadcast already sent: once
  const rainDue = () => {
    for (const [t, k] of words)
      if (t <= shownUpTo) {
        words.delete(t);
        rained.add(t);
        rain(k);
      }
  };

  const clearThinking = () => {
    if (thinking) {
      thinking.remove();
      thinking = null;
    }
  };
  const showThinking = (side: Side) => {
    if (state === "ended" || thinking) return;
    list.insertAdjacentHTML(
      "beforeend",
      `<li class="msg thinking" data-side="${side}" aria-label="${side} is thinking"><span class="dots"><i></i><i></i><i></i></span></li>`,
    );
    thinking = list.lastElementChild as HTMLElement;
    keepInView(thinking);
    state = "thinking";
  };
  const finish = () => {
    if (state === "ended" || !done) return;
    // the last turn just finished typing: a beat to read it before the seal comes down
    if (state !== "finishing") {
      state = "finishing";
      const d = done;
      timers.readBeat = setTimeout(
        () => {
          timers.readBeat = null;
          seal(d);
        },
        reduced ? 0 : T.READ_BEAT,
      );
    }
  };
  const seal = (d: DoneEvent) => {
    state = "ended";
    shownUpTo = Infinity;
    rainDue();
    timers.clearAll();
    clearThinking();
    unfocusAll(list);
    if (stateEl && !opts.keepState) stateEl.textContent = "final";
    sealBout(bout, d);
    music?.end({ result: d.result, reason: d.reason });
    if (isTv()) tv.onBoutEnd(d.crowned ? T.CROWN_END_HOLD : T.END_HOLD); // the stage plays the fall, then the next bout the moment it is on (a crowning lingers)
  };
  // show the next queued turn now; snap whatever is still typing
  const pump = () => {
    if (held) return; // the ceremony: nothing shows until the wave
    if (!queue.length) {
      if (done) finish();
      else if (lastSide) {
        showThinking(other(lastSide));
        if (opts.onIdle) opts.onIdle();
      }
      return;
    }
    if (typing) {
      typing.cancel();
      typing = null;
    }
    const m = queue.shift() as TurnEvent;
    shownUpTo = Math.max(shownUpTo, m.turn - 1);
    rainDue(); // the turn before is on the screen now, snapped or typed
    clearThinking();
    list.insertAdjacentHTML("beforeend", msgHtml(m, maxTokens));
    const li = list.lastElementChild as HTMLElement;
    li.dataset.full = String(m.content.length); // the whole, for the stage to read the stream's progress against
    lastSide = m.side;
    bout.dispatchEvent(new window.CustomEvent<TurnDetail>("basho:turn", { detail: { turn: m } })); // the panel's card draws it (panel.ts)
    music?.turn({
      turn: m.turn,
      side: m.side,
      len: m.content.length,
      tokens: m.tokens,
      echoed: !!m.echoes?.length,
      text: m.content,
    });
    if (!queue.length) tap(); // a turn lands (quiet when we are catching up)
    setClock(clock, m.turn);
    focusMsg(list, li);
    const cap = typeCapFor(queue.length, !!done && !queue.length, opts.cap ?? T.TYPE_CAP_LIVE);
    let typingDone = false; // also set when snapped, so the follow nudge stops
    followWhile(li, () => typingDone);
    state = "typing";
    const h = typeInto($(".body", li) as HTMLElement, m.content, cap, () => {
      typingDone = true;
      typing = null;
      shownUpTo = Math.max(shownUpTo, m.turn);
      rainDue();
      timers.turnGap = setTimeout(() => {
        timers.turnGap = null;
        pump();
      }, T.TURN_GAP); // straight on
    });
    if (!typingDone)
      typing = {
        cancel() {
          typingDone = true;
          h.cancel();
        },
      };
  };
  const p = {
    /**
     * The band strikes up for the bout on the page. Joined mid-bout (the tv changed channel to a bout already under way,
     * or the page loaded late), it hears the turns already on the page first, so it plays on from where the bout is:
     * it used to strike up only for an empty ring and sat the whole bout out otherwise. A bout
     * with its verdict has had its song.
     */
    open() {
      if (done || !music) return;
      const ds = bout.dataset as BoutDataset;
      music.start({
        id: ds.bout,
        east: slugOf("east"),
        west: slugOf("west"),
        maxTurns: +(ds.maxTurns || 50),
        maxTokens,
        vamp: true, // the tv: another bout follows, the band plays on between them and blends into it (#597)
      });
      for (const li of list.querySelectorAll<HTMLElement>("li.msg:not(.thinking)")) {
        const d = li.dataset;
        if (d.side !== "east" && d.side !== "west") continue;
        const text = $(".body", li)?.textContent || "";
        music.turn({
          turn: +(d.turn || 0),
          side: d.side,
          len: text.length,
          tokens: +(d.tokens || 0),
          echoed: !!d.echo,
          text,
        });
      }
    },
    push(m: TurnEvent) {
      queue.push(m);
      bout.dispatchEvent(new window.CustomEvent<TurnDetail>("basho:turn-in", { detail: { turn: m } })); // the shot clock restarts on the arrival, not the typing
      if (!typing) pump(); // a turn mid-type finishes naturally; the new one waits in line
    },
    end(d: DoneEvent) {
      done = d;
      if (!typing) pump();
    },
    /** the secret word was said on a turn: rain when that turn is on the screen (at once if it already is) */
    kotodama(k: KotodamaEvent) {
      if (rained.has(k.turn)) return;
      words.set(k.turn, k);
      rainDue();
    },
    state(): PlayerState {
      return state;
    },
  };
  if (lastSide && !done && !held) showThinking(other(lastSide)); // held for the ceremony: the dots come with the wave (release pumps)
  ring.onTeardown(() => {
    timers.clearAll();
    if (typing) typing.cancel();
    bout.removeEventListener("basho:hajime", release);
  });
  return p;
}

/** the one li the server rendered, taken back so it can arrive through the player */
export function takeBack(li: HTMLElement): TurnEvent {
  const body = $(".body", li);
  const m: TurnEvent = {
    turn: +(li.dataset.turn || 0),
    side: li.dataset.side as Side,
    content: body?.textContent || "",
    tokens: +(li.dataset.tokens || 0),
    cut: false,
  };
  // the distances, when the server embedded the turn: a replay's sparklines draw from them
  const drift = +(li.dataset.drift ?? NaN),
    swing = +(li.dataset.swing ?? NaN);
  if (Number.isFinite(drift)) m.drift = drift;
  if (Number.isFinite(swing)) m.swing = swing;
  const echoes = parseEchoAttr(body?.dataset.echo);
  if (echoes.length) m.echoes = echoes;
  li.remove();
  return m;
}

export function initLive(bout: HTMLElement | null, tv: TvHooks) {
  if (!bout || !window.EventSource) return;
  const id = (bout.dataset as BoutDataset).bout;
  const list = $("[data-transcript]", bout) as HTMLElement;
  const seen = new Set([...list.querySelectorAll("li.msg")].map((li) => (li as HTMLElement).dataset.turn));
  // a fresh bout with turn 1 already on the page: take it back and let it arrive through the player, typed
  let first: TurnEvent | null = null;
  if ((bout.dataset as BoutDataset).fresh) {
    const li = list.querySelector("li.msg") as HTMLElement | null;
    if (li) first = takeBack(li);
  }
  bout.classList.add("playing"); // the server-rendered turns are ours now: anything new is meant to be seen
  // on the stage the pair always walk on and throw the salt first, a bout joined mid-way too (clouds.ts `opening`, and the
  // tv's channel change respawns the pair): the words that come wait for the referee's wave, then catch up. On the flat
  // ring there is no ceremony to wait for
  const p = player(bout, tv, { hold: wantStage() });
  p.open();
  if (first) p.push(first);

  // the stream, re-opened if it goes quiet for too long (the browser retries a dropped
  // connection on its own; a server that went away mid-bout does not say so)
  let es: EventSource | null = null;
  let watchdog: ReturnType<typeof setTimeout> | null = null;
  const quiet = () => {
    if (watchdog) clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      if (p.state() !== "ended") {
        console.warn("bout stream quiet; reopening");
        connect();
      }
    }, T.STREAM_SILENCE);
  };
  const connect = () => {
    if (es) es.close();
    es = new EventSource((bout.dataset as BoutDataset).events || `/bout/${id}/events`); // a practice bout streams from its own address (#576)
    ring.stream(es);
    es.addEventListener("turn", (e) => {
      quiet();
      const m = parseTurn(e.data);
      if (!m || seen.has(String(m.turn))) return;
      seen.add(String(m.turn));
      p.push(m);
    });
    es.addEventListener("kotodama", (e) => {
      const k = parseKotodama(e.data);
      if (k) p.kotodama(k);
    });
    es.addEventListener("lagged", () => {
      console.warn("bout stream lagged; resyncing");
      connect();
    });
    es.addEventListener("done", (e) => {
      es?.close();
      if (watchdog) clearTimeout(watchdog);
      const d = parseDone(e.data);
      if (d) p.end(d);
    });
    es.onerror = () => {
      /* the browser retries; Last-Event-ID resumes */
    };
    quiet();
  };
  connect();
  ring.onTeardown(() => {
    if (watchdog) clearTimeout(watchdog);
  });
}
